import type { BlockedDate } from "@/src/shared/types/blockedDate";
import type { IListing, IListingBadge } from "@/src/shared/types/listing";
import { listingBadgeService } from "@/src/shared/services/listingBadge";
import type { IListingContact, IListingContactSection } from "./types";

export interface IHouseRules {
  checkInOut: string[];
  maxGuests: string | null;
  rules: string[];
  additional: string | null;
}

type IHouseRulesSource = Pick<
  IListing,
  "check_in" | "check_out" | "max_guest" | "rules" | "house_policy"
>;

// Some hosts type the whole phrase ("Check-in after 2 PM"), so it is shown as-is.
const timeLine = (
  value: string | null | undefined,
  phrase: string,
  prefix: string,
) => {
  const time = value?.trim();
  if (!time) return null;
  return time.toLowerCase().startsWith(prefix) ? time : `${phrase} ${time}`;
};

const DAY_MS = 24 * 60 * 60 * 1000;

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const countLabel = (count: number, singular: string, plural: string) =>
  count > 0 ? `${count} ${count === 1 ? singular : plural}` : "";

/**
 * The app's Guest Favourite stay banner (`ListingGuestFavoriteStayBanner`): one Lottie on a
 * 1400×1080 canvas whose frames 0–60 are the intro and 60–90 the docking, at 29.97 fps.
 * Sizes are canvas px; the banner scales the canvas to the screen's width.
 */
export const STAY_BANNER = {
  canvas: { width: 1400, height: 1080 },
  /** The docked strip's bottom edge: the banner's height, sitting on the booking bar. */
  dockedStripBottom: 686.5,
  introEnd: 60,
  dockStart: 60,
  dockEnd: 90,
  fps: 29.97,
  /** The formed pill, where the shadow goes. */
  pill: { left: 107, top: 469, width: 1176, height: 142, radius: 45 },
  pillLayer: "Shape Layer 2",
  pillBorderWidth: 4,
  /** `listingGuestFavoriteStayBorder`, `#DDDDDD`. */
  pillBorderColor: [221 / 255, 221 / 255, 221 / 255, 1],
  pillFormedFrame: 32,
  shadowFadeInFrames: 16,
  dockShadowFadeOutShare: 0.4,
  shadow: { blur: 64, spread: 10, offsetY: 6 },
  riseFrames: 14,
  /** The intro hurries to its end this fast when the page is scrolled before it ends. */
  fastForwardSeconds: 0.2,
} as const;

/** A linear keyframe's tangents. */
const LINEAR = { i: { x: [1], y: [1] }, o: { x: [0], y: [0] } };

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
/** An ease-out for the shadow's fade-in (the app's `Curves.easeOut`, approximated). */
const easeOut = (t: number) => 1 - (1 - t) * (1 - t);
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

export const listingService = {
  /**
   * Split blocked nights into runs of consecutive days. The first night of each run is still
   * free to check out on (a guest can leave the morning it starts); a single night is both.
   */
  groupBlockedDates(dates: BlockedDate[]): {
    blockedDates: BlockedDate[];
    checkoutOnlyDates: string[];
  } {
    // Duplicates keep the last occurrence; then oldest first.
    const sorted = Array.from(
      new Map(dates.map((d) => [d.date, d])).values(),
    ).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    const runs: BlockedDate[][] = [];
    for (const date of sorted) {
      const run = runs[runs.length - 1];
      const last = run?.[run.length - 1];
      if (
        run &&
        last &&
        new Date(date.date).getTime() - new Date(last.date).getTime() === DAY_MS
      )
        run.push(date);
      else runs.push([date]);
    }

    const blockedDates: BlockedDate[] = [];
    const checkoutOnlyDates: string[] = [];
    for (const [first, ...rest] of runs) {
      if (!first) continue;
      checkoutOnlyDates.push(first.date);
      blockedDates.push(...(rest.length ? rest : [first]));
    }
    return { blockedDates, checkoutOnlyDates };
  },

  /**
   * The line under the title, as the app words it (`ListingFormatters.propertyTypeLabel`):
   * "Room in an Apartment", "Full Apartment", "Hotel Room"…; else the raw type name.
   */
  propertyTypeLabel(propertyType: string, placeType: string): string {
    const property = propertyType.trim().toLowerCase().replace(/-/g, " ");
    const place = placeType.trim().toLowerCase().replace(/-/g, " ");
    const isRoom = place.includes("room");
    const isEntire = place.includes("entire");
    if (property === "hotel portfolio") return "Rooms in a Hotel";
    if (property === "apartment" && isRoom) return "Room in an Apartment";
    if (property === "apartment" && isEntire) return "Full Apartment";
    if (property === "hotel" && isRoom) return "Hotel Room";
    if (property === "studio apartment" && isEntire) return "Studio Apartment";
    return propertyType;
  },

  /** The app's bath wording: 0.5 → "Non-attached Bath", 1 → "Attached Bath", n → "n Bath". */
  bathLabel(bathroom: number | null | undefined): string {
    if (!bathroom) return "";
    if (bathroom === 0.5) return "Non-attached Bath";
    if (bathroom === 1) return "Attached Bath";
    return `${bathroom} Bath`;
  },

  /** "4 guests · 2 bedrooms · 3 beds · Attached Bath"; empty and zero parts drop out. */
  specsSummary(
    listing: Pick<IListing, "max_guest" | "bedroom" | "beds" | "bathroom">,
  ): string {
    return [
      countLabel(listing.max_guest, "guest", "guests"),
      countLabel(listing.bedroom, "bedroom", "bedrooms"),
      countLabel(listing.beds, "bed", "beds"),
      listingService.bathLabel(listing.bathroom),
    ]
      .filter(Boolean)
      .join(" · ");
  },

  /** The Guest Favourite badge, or null: a listing is one exactly when it carries it. */
  guestFavouriteBadge(badges: IListingBadge[] | null | undefined) {
    return (
      badges?.find((badge) =>
        listingBadgeService.isHighlyRecommended(badge.slug),
      ) ?? null
    );
  },

  /** "1,234 reviews" / "1 review", thousands separated as in the app. */
  reviewCountLabel(count: number): string {
    return `${count.toLocaleString("en-US")} ${count === 1 ? "review" : "reviews"}`;
  },

  /** "Identity verified · 3 years hosting"; the years drop out in the first year. */
  hostedByLine(yearsHosting: number | null | undefined): string {
    const years = yearsHosting ?? 0;
    return years > 0
      ? `Identity verified · ${years} ${years === 1 ? "year" : "years"} hosting`
      : "Identity verified";
  },

  /**
   * The host card's tenure, as the app's `_tenureLabel`: a host under a year shows
   * "Started hosting since {year}" instead of "0 Years on Travela".
   */
  hostTenure(
    yearsHosting: number | null | undefined,
    currentYear: number,
  ): { years: number } | { since: number } {
    const years = yearsHosting ?? 0;
    return years < 1 ? { since: currentYear - years } : { years };
  },

  /** The app's response time: the label, else whole hours from the seconds, else "2 Hours". */
  responseTimeLabel(
    response:
      { p90_label: string | null; p90_secs: number | null } | null | undefined,
  ): string {
    if (response?.p90_label) return response.p90_label;
    if (response?.p90_secs) {
      const hours = Math.max(1, Math.ceil(response.p90_secs / 3600));
      return `${hours} ${hours === 1 ? "Hour" : "Hours"}`;
    }
    return "2 Hours";
  },

  /** The booking bar's dates: "12–15 Oct" in one month, "28 Oct – 2 Nov" across two. */
  stayDatesLabel(checkIn: Date, checkOut: Date): string {
    const inMonth = MONTHS[checkIn.getMonth()];
    const outMonth = MONTHS[checkOut.getMonth()];
    return checkIn.getMonth() === checkOut.getMonth() &&
      checkIn.getFullYear() === checkOut.getFullYear()
      ? `${checkIn.getDate()}–${checkOut.getDate()} ${outMonth}`
      : `${checkIn.getDate()} ${inMonth} – ${checkOut.getDate()} ${outMonth}`;
  },

  /** Build the app's house-rules sections; null when the listing has none. */
  buildHouseRules(listing: IHouseRulesSource): IHouseRules | null {
    const checkInOut = [
      timeLine(listing.check_in, "Check-in after", "check"),
      timeLine(listing.check_out, "Checkout before", "checkout"),
    ].filter((line): line is string => line !== null);

    const guests = listing.max_guest;
    const maxGuests =
      guests > 0
        ? `${guests} ${guests === 1 ? "guest" : "guests"} maximum`
        : null;
    const rules = (listing.rules ?? [])
      .map((rule) => rule.trim())
      .filter(Boolean);
    const additional = listing.house_policy?.trim() || null;

    if (!checkInOut.length && !maxGuests && !rules.length && !additional)
      return null;
    return { checkInOut, maxGuests, rules, additional };
  },

  /** The contact screen's sections, without the empty ones (the app hides those too). */
  contactSections(
    contact: IListingContact | null | undefined,
  ): IListingContactSection[] {
    return (contact?.sections ?? []).filter(
      (section) => section.items.length > 0,
    );
  },

  /**
   * "Contact Farhan Hassan": the host's full name (the contact API's host, else the
   * listing's); without a name, the API's title, else "Contact host".
   */
  contactTitle(
    contact: IListingContact | null | undefined,
    host: { first_name: string | null; last_name: string | null } | null,
  ): string {
    const fullName = (person?: typeof host) =>
      [person?.first_name, person?.last_name]
        .map((part) => part?.trim())
        .filter(Boolean)
        .join(" ");
    const name = fullName(contact?.host) || fullName(host);
    if (name) return `Contact ${name}`;
    return contact?.title || "Contact host";
  },

  /** The message screen's prompt, as the app's `BookingRequestController.messagePrompt`. */
  messagePrompt(hostName: string): string {
    return `Tell ${hostName || "the host"} who will be joining you, purpose of travel, and ask anything you want`;
  },

  /** "3 guests", "1 guest, 1 infant": adults and children are guests, infants apart. */
  guestsLabel(guests: { adults: number; children: number; infants: number }) {
    const count = guests.adults + guests.children;
    const text = `${count} ${count === 1 ? "guest" : "guests"}`;
    if (!guests.infants) return text;
    return `${text}, ${guests.infants} ${guests.infants === 1 ? "infant" : "infants"}`;
  },

  /**
   * The iOS back swipe: let go past a third of the width, or flick right; a flick back
   * left cancels even past the third. `velocity` is px/ms, positive to the right.
   */
  swipeBackCommits(progress: number, velocity: number): boolean {
    if (velocity > 0.5) return true;
    return progress > 1 / 3 && velocity > -0.3;
  },

  /** Whether the guests fit the listing's maximums (the app's `isGuestInfoWithinLimits`). */
  guestsFit(
    guests: { adults: number; children: number; infants: number },
    max: { adults: number; children: number; infants: number },
  ): boolean {
    const within = (count: number, limit: number) => !limit || count <= limit;
    return (
      within(guests.adults, max.adults) &&
      within(guests.children, max.children) &&
      within(guests.infants, max.infants)
    );
  },

  /**
   * The Guest Favourite stay banner's Lottie with the app's pill border: its delegates
   * draw "Shape Layer 2"'s stroke 4px `#DDDDDD`, fully opaque through the intro and
   * fading out over the docking. The source is left untouched.
   */
  stayBannerWithPill<T extends object>(animation: T): T {
    const copy = JSON.parse(JSON.stringify(animation)) as {
      layers?: { nm?: string; shapes?: unknown[] }[];
    };
    const pill = copy.layers?.find(
      (layer) => layer.nm === STAY_BANNER.pillLayer,
    );
    const strokes: Record<string, unknown>[] = [];
    const collect = (items: unknown[] | undefined) =>
      items?.forEach((item) => {
        if (!item || typeof item !== "object") return;
        const shape = item as { ty?: string; it?: unknown[] };
        if (shape.ty === "st") strokes.push(shape as Record<string, unknown>);
        collect(shape.it);
      });
    collect(pill?.shapes);
    for (const stroke of strokes) {
      stroke.c = { a: 0, k: STAY_BANNER.pillBorderColor };
      stroke.w = { a: 0, k: STAY_BANNER.pillBorderWidth };
      stroke.o = {
        a: 1,
        k: [
          { t: 0, s: [100], i: LINEAR.i, o: LINEAR.o },
          { t: STAY_BANNER.dockStart, s: [100], i: LINEAR.i, o: LINEAR.o },
          { t: STAY_BANNER.dockEnd, s: [0] },
        ],
      };
    }
    return copy as T;
  },

  /**
   * The pill's shadow (drawn outside the Lottie, as the app does): it fades in over the
   * 16 frames after the pill forms at frame 32, and out over the first 40% of the docking.
   * `frame` is the Lottie's frame, docking from 60 to 90.
   */
  stayBannerShadowOpacity(frame: number): number {
    if (frame >= STAY_BANNER.dockStart) {
      const dock =
        (frame - STAY_BANNER.dockStart) /
        (STAY_BANNER.dockEnd - STAY_BANNER.dockStart);
      return clamp01(1 - dock / STAY_BANNER.dockShadowFadeOutShare);
    }
    const fadeIn =
      (frame - STAY_BANNER.pillFormedFrame) / STAY_BANNER.shadowFadeInFrames;
    return easeOut(clamp01(fadeIn));
  },

  /**
   * How far below its place the banner sits, in canvas px: the intro rises it out from
   * under the booking bar over its first 14 frames (`easeOutCubic`); docked, it stays put.
   */
  stayBannerRiseOffset(frame: number): number {
    if (frame >= STAY_BANNER.dockStart) return 0;
    const rise = clamp01(frame / STAY_BANNER.riseFrames);
    const hidden = STAY_BANNER.dockedStripBottom - STAY_BANNER.pill.top;
    return hidden * (1 - easeOutCubic(rise));
  },
};
